import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { apiErrorMessage } from '../../../utils/errors';
import { useCurrentUser } from '../../../features/auth/api';
import {
  useInviteOrgMember,
  useListOrgMembers,
  useRemoveOrgMember,
  useUpdateOrgMemberRole,
} from '../../../features/org-members/api';

function roleLabel(role: string) {
  if (role === 'ORG_OWNER') return 'Owner';
  if (role === 'ORG_ADMIN') return 'Sales Rep';
  return 'Member';
}

function statusLabel(status: string) {
  if (status === 'INVITED') return 'Invited';
  if (status === 'EXPIRED') return 'Expired';
  if (status === 'DISABLED') return 'Disabled';
  return 'Active';
}

function statusBadgeStyle(status: string) {
  if (status === 'INVITED') {
    return {
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      color: '#1d4ed8',
    };
  }

  if (status === 'EXPIRED' || status === 'DISABLED') {
    return {
      background: '#fef2f2',
      border: '1px solid #fecaca',
      color: '#b91c1c',
    };
  }

  return {
    background: '#ecfdf5',
    border: '1px solid #bbf7d0',
    color: '#047857',
  };
}

export default function OrganizationUsersTab() {
  const { data: me } = useCurrentUser();
  const canManage = me?.type === 'ORG_OWNER' || me?.type === 'ORG_ADMIN';
  const isOrgOwner = me?.type === 'ORG_OWNER';

  const { data: users = [], isLoading } = useListOrgMembers(!!canManage);
  const invite = useInviteOrgMember();
  const updateRole = useUpdateOrgMemberRole();
  const remove = useRemoveOrgMember();

  const [showInvite, setShowInvite] = useState(false);

  const sorted = useMemo(() => {
    const rank = {
      ORG_OWNER: 0,
      ORG_ADMIN: 1,
      ORG_USER: 2,
    };

    return [...users].sort((a, b) => {
      const roleDiff = (rank[a.role] ?? 9) - (rank[b.role] ?? 9);
      if (roleDiff !== 0) return roleDiff;

      const aStatus = a.user?.status || 'ACTIVE';
      const bStatus = b.user?.status || 'ACTIVE';

      const statusRank = {
        ACTIVE: 0,
        INVITED: 1,
        EXPIRED: 2,
        DISABLED: 3,
      };

      return ((statusRank as any)[aStatus] ?? 9) - ((statusRank as any)[bStatus] ?? 9);
    });
  }, [users]);

  if (!canManage) {
    return (
      <div className="card">
        <h2 className="card__title">Users</h2>
        <p className="card__subtitle">Only admins can manage team users.</p>
        <div className="muted" style={{ marginTop: 12 }}>
          You don’t have permission to view this page.
        </div>
      </div>
    );
  }

  const resendInvite = async (user: any) => {
    try {
      await invite.mutateAsync({
        email: user.user.email,
        role: user.role,
      });

      toast.success('Invite resent.');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to resend invite.'));
    }
  };

  return (
    <div className="card">
      <div className="flex-between" style={{ marginBottom: 12 }}>
        <div>
          <h2 className="card__title">Users</h2>
          <p className="card__subtitle">Manage organization admins and members.</p>
        </div>

        <button className="btn btn--primary" onClick={() => setShowInvite(true)}>
          Invite user
        </button>
      </div>

      <div className="card card--bleed-top">
        {isLoading ? (
          <div className="skeleton" style={{ height: 200 }} />
        ) : sorted.length === 0 ? (
          <div className="muted" style={{ padding: 16 }}>
            No users yet.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Updated</th>
                <th className="ta-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {sorted.map((u) => {
                const isInvite = !!u.invite || !u.user?.firebaseUid;
                const status = u.user?.status || 'ACTIVE';
                const isOwner = u.role === 'ORG_OWNER';
                const canEditTarget = !isOwner && !isInvite;

                return (
                  <tr key={`${isInvite ? 'invite' : 'member'}-${u.id}`}>
                    <td>{u.user?.name || '—'}</td>

                    <td>{u.user?.email || '—'}</td>

                    <td>
                      <span
                        className="badge"
                        style={{
                          background: '#f3f4f6',
                          border: '1px solid #e5e7eb',
                        }}
                      >
                        {roleLabel(u.role)}
                      </span>
                    </td>

                    <td>
                      <span className="badge" style={statusBadgeStyle(status)}>
                        {statusLabel(status)}
                      </span>
                    </td>

                    <td>{u.createdAt ? new Date(u.createdAt).toLocaleString() : '—'}</td>

                    <td className="ta-right">
                      <div className="actions" style={{ justifyContent: 'flex-end', gap: 8 }}>
                        <div className="form-control" style={{ marginBottom: 0, minWidth: 120 }}>
                          <select
                            value={u.role}
                            disabled={!canEditTarget || updateRole.isPending}
                            onChange={(e) => {
                              const next = e.target.value;
                              if (!['ORG_ADMIN', 'ORG_USER'].includes(next)) return;

                              updateRole.mutate(
                                { uid: u.id, role: next as 'ORG_ADMIN' | 'ORG_USER' },
                                {
                                  onSuccess: () => toast.success('Role updated.'),
                                  onError: (err) =>
                                    toast.error(apiErrorMessage(err, 'Failed to update role')),
                                }
                              );
                            }}
                          >
                            <option value="ORG_OWNER" disabled>
                              Owner
                            </option>
                            <option value="ORG_ADMIN">Sales Rep</option>
                            {/* <option value="ORG_USER">Member</option> */}
                          </select>
                        </div>

                        {isInvite ? (
                          <button
                            className="btn btn--ghost"
                            disabled={invite.isPending}
                            onClick={() => resendInvite(u)}
                          >
                            {invite.isPending ? 'Sending…' : 'Resend invite'}
                          </button>
                        ) : (
                          <button
                            className="btn btn--danger"
                            disabled={!canEditTarget || remove.isPending || !isOrgOwner}
                            onClick={() => {
                              if (
                                !window.confirm(`Remove ${u.user?.email} from this organization?`)
                              ) {
                                return;
                              }

                              remove.mutate(u.id, {
                                onSuccess: () => toast.success('User removed.'),
                                onError: (err) =>
                                  toast.error(apiErrorMessage(err, 'Failed to remove user')),
                              });
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showInvite && (
        <InviteUserModal
          onClose={() => setShowInvite(false)}
          onInvite={async (payload: any) => {
            try {
              await invite.mutateAsync(payload);
              toast.success('Invite sent.');
              setShowInvite(false);
            } catch (e) {
              toast.error(apiErrorMessage(e, 'Invite failed'));
            }
          }}
          isInviting={invite.isPending}
        />
      )}
    </div>
  );
}

function InviteUserModal({
  onClose,
  onInvite,
  isInviting,
}: {
  onClose: () => void;
  onInvite: (payload: any) => Promise<void> | void;
  isInviting?: boolean;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('ORG_ADMIN');

  const canSubmit = email.trim().length > 3 && ['ORG_ADMIN', 'ORG_USER'].includes(role);

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onClose} />

      <div className="modal__panel" style={{ maxWidth: 520 }}>
        <div className="modal__header">
          <h3 className="modal__title">Invite user</h3>

          <button className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="modal__body">
          <div className="form-control">
            <label>Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              type="email"
              autoFocus
            />
          </div>

          <div className="form-control" style={{ marginTop: 12 }}>
            <label>Role</label>

            <select value={role} onChange={(e) => setRole(e.target.value)}>
              {/* <option value="ORG_USER">Member</option> */}
              <option value="ORG_ADMIN">Sales Rep</option>
            </select>

            <div className="muted" style={{ marginTop: 6 }}>
              Admins can invite/manage users. Members cannot.
            </div>
          </div>
        </div>

        <div className="modal__footer">
          <div className="actions" style={{ gap: 8 }}>
            <button
              className="btn btn--primary"
              disabled={!canSubmit || isInviting}
              onClick={() => onInvite({ email: email.trim(), role })}
            >
              {isInviting ? 'Sending…' : 'Send invite'}
            </button>

            <button className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
